// Song-Editor (rechte Detailspalte): Daten, Cues, Kategorien, Ordner & Versionen, Sendezeiten, Tags.

import { useEffect, useState } from "react";
import { api, fmtMs, fmtTime, toast, useLoad } from "../../api";
import { Field, Switch } from "../../ui";
import { CueEditor } from "./CueEditor";
import { TimingGrid, timingText } from "../../schedule";
import { ContainerEditor } from "./Container";

const KINDS: [string, string][] = [
  ["music", "Musik"], ["jingle", "Jingle"], ["dropper", "Dropper"], ["station_id", "Station-ID"], ["imaging", "Sweeper/Imaging"], ["ad", "Werbung"],
  ["news_bed", "Nachrichtenbett"], ["spoken", "Wort"], ["voicetrack", "Voicetrack"],
];
const SCALARS: [string, string][] = [["mood", "Stimmung"], ["energy", "Energie"], ["tempo", "Tempo"], ["texture", "Textur"]];

function Scale({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <div className="row" style={{ gap: 4 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" className="btn small" onClick={() => onChange(value === n ? null : n)}
          style={{ minWidth: 30, justifyContent: "center", background: value != null && n <= value ? "var(--accent-2)" : undefined, borderColor: value === n ? "var(--accent)" : undefined }}>
          {n}
        </button>
      ))}
    </div>
  );
}

function Data({ song, onSaved }: { song: any; onSaved: () => void }) {
  const { data: fields } = useLoad<any[]>("/api/custom-fields");
  const [v, setV] = useState<any>(song);
  useEffect(() => setV(song), [song]);
  const dirty = JSON.stringify(v) !== JSON.stringify(song);
  const set = (patch: any) => setV({ ...v, ...patch });
  const save = async () => {
    const body: any = {};
    for (const k of ["kind", "title", "artist", "album", "year", "isrc", "bpm", "label", "genre", "era", "rds_title", "dab_title", "voice_gender", "soundcode", "end_type", "mood", "energy", "tempo", "texture", "released", "custom"]) body[k] = v[k];
    try {
      await api(`/api/media/${song.id}`, { method: "PUT", body });
      toast("Gespeichert");
      onSaved();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const upload = async (f: File) => {
    try {
      await api(`/api/media/${song.id}/cover`, { raw: f, type: "application/x-radiocast-upload" });
      toast("Cover gesetzt");
      onSaved();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <div className="stack">
      <div className="row">
        <div className="onair" style={{ gap: 12 }}>
          {song.cover ? <img src={`/api/media/${song.id}/cover?v=${song.updated_at}`} alt="" style={{ width: 72, height: 72, borderRadius: 8, objectFit: "cover" }} /> : <div className="cover">{(song.artist || song.title || "?").slice(0, 1)}</div>}
        </div>
        <label className="btn small">Cover wählen …<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }} /></label>
        <span className="spacer" />
        <Switch checked={!!v.released} onChange={(x) => set({ released: x })} label={v.released ? "für Sendung freigegeben" : "wartet auf Freigabe"} />
      </div>
      <div className="form">
        <Field label="Titel"><input type="text" value={v.title ?? ""} onChange={(e) => set({ title: e.target.value })} /></Field>
        <Field label="Interpret"><input type="text" value={v.artist ?? ""} onChange={(e) => set({ artist: e.target.value })} /></Field>
        <Field label="Album"><input type="text" value={v.album ?? ""} onChange={(e) => set({ album: e.target.value })} /></Field>
        <Field label="Art">
          <select value={v.kind} onChange={(e) => set({ kind: e.target.value })}>{KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </Field>
        <Field label="Jahr"><input type="number" value={v.year ?? ""} onChange={(e) => set({ year: e.target.value ? Number(e.target.value) : null })} /></Field>
        <Field label="Ära" hint="z. B. 80er, 90er, Aktuell"><input type="text" value={v.era ?? ""} onChange={(e) => set({ era: e.target.value })} /></Field>
        <Field label="Genre"><input type="text" value={v.genre ?? ""} onChange={(e) => set({ genre: e.target.value })} /></Field>
        <Field label="Label"><input type="text" value={v.label ?? ""} onChange={(e) => set({ label: e.target.value })} /></Field>
        <Field label="ISRC"><input type="text" value={v.isrc ?? ""} onChange={(e) => set({ isrc: e.target.value.toUpperCase() })} /></Field>
        <Field label="BPM"><input type="number" step="0.1" value={v.bpm ?? ""} onChange={(e) => set({ bpm: e.target.value ? Number(e.target.value) : null })} /></Field>
        <Field label="RDS-Titel" hint="kurz, für Radiotext"><input type="text" maxLength={64} value={v.rds_title ?? ""} onChange={(e) => set({ rds_title: e.target.value })} /></Field>
        <Field label="DAB-Titel"><input type="text" maxLength={128} value={v.dab_title ?? ""} onChange={(e) => set({ dab_title: e.target.value })} /></Field>
        {SCALARS.map(([k, l]) => (
          <Field key={k} label={`${l} (1–5)`}><Scale value={v[k] ?? null} onChange={(x) => set({ [k]: x })} /></Field>
        ))}
        <Field label="Stimme">
          <select value={v.voice_gender ?? ""} onChange={(e) => set({ voice_gender: e.target.value })}>
            <option value="">–</option><option value="m">männlich</option><option value="f">weiblich</option><option value="mix">gemischt</option><option value="inst">instrumental</option>
          </select>
        </Field>
        <Field label="Endtyp">
          <select value={v.end_type ?? ""} onChange={(e) => set({ end_type: e.target.value })}>
            <option value="">–</option><option value="cold">kalt (hartes Ende)</option><option value="fade">Ausblende</option><option value="sustain">Ausklang</option>
          </select>
        </Field>
        <Field label="Soundcode"><input type="text" value={v.soundcode ?? ""} onChange={(e) => set({ soundcode: e.target.value })} /></Field>
        {(fields ?? []).map((f) => (
          <Field key={f.id} label={f.label}>
            {f.type === "bool" ? (
              <Switch checked={!!v.custom?.[f.key]} onChange={(x) => set({ custom: { ...v.custom, [f.key]: x } })} />
            ) : (
              <input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={v.custom?.[f.key] ?? ""} onChange={(e) => set({ custom: { ...v.custom, [f.key]: e.target.value } })} />
            )}
          </Field>
        ))}
      </div>
      <div className="small muted">
        {fmtMs(song.duration_ms)} · {song.loudness_lufs != null ? `${song.loudness_lufs.toFixed(1)} LUFS` : "Lautheit nicht gemessen"} · Datei {song.original_name} · hinzugefügt {fmtTime(song.created_at)}{song.uploaded_by ? ` von ${song.uploaded_by}` : ""} · {song.plays} × gespielt{song.last_played ? `, zuletzt ${fmtTime(song.last_played)}` : ""}
        {song.status === "error" && <div style={{ color: "var(--bad)" }}>Analyse-Fehler: {song.error}</div>}
      </div>
      <div className="savebar">
        <span className="small muted">{dirty ? "Ungespeicherte Änderungen" : "Alle Änderungen gespeichert"}</span>
        <span className="spacer" />
        <button className="btn" disabled={!dirty} onClick={() => setV(song)}>Verwerfen</button>
        <button className="btn primary" disabled={!dirty} onClick={save}>Speichern</button>
      </div>
    </div>
  );
}

function Categories({ song, onSaved }: { song: any; onSaved: () => void }) {
  const { data: cats } = useLoad<any[]>("/api/categories");
  const [sel, setSel] = useState<string[]>(song.categories ?? []);
  useEffect(() => setSel(song.categories ?? []), [song]);
  const toggle = (id: string) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const save = async () => {
    await api(`/api/media/${song.id}`, { method: "PUT", body: { categories: sel } });
    toast("Kategorien gespeichert");
    onSaved();
  };
  return (
    <div className="stack">
      <p className="muted small" style={{ margin: 0 }}>Kategorien bestimmen, was gesendet wird. Die Zugehörigkeit gilt für diesen Sender.</p>
      {(cats ?? []).map((c) => (
        <label key={c.id} className="row" style={{ cursor: "pointer" }}>
          <input type="checkbox" checked={sel.includes(c.id)} onChange={() => toggle(c.id)} />
          <span className="led" style={{ background: c.color }} /> {c.name} <span className="muted small">{c.songs} Songs</span>
        </label>
      ))}
      {!cats?.length && <div className="muted">Noch keine Kategorien.</div>}
      <div className="row"><span className="spacer" /><button className="btn primary" onClick={save}>Speichern</button></div>
    </div>
  );
}

function FoldersVersions({ song, onSaved }: { song: any; onSaved: () => void }) {
  const { data: folders } = useLoad<any[]>("/api/folders");
  const [sel, setSel] = useState<string[]>(song.folders ?? []);
  useEffect(() => setSel(song.folders ?? []), [song]);
  const path = (f: any): string => {
    const parent = (folders ?? []).find((x) => x.id === f.parent_id);
    return parent ? `${path(parent)} / ${f.name}` : f.name;
  };
  const save = async () => {
    await api(`/api/media/${song.id}`, { method: "PUT", body: { folders: sel } });
    toast("Ordner gespeichert");
    onSaved();
  };
  return (
    <div className="stack">
      <h3>Ordner</h3>
      <p className="muted small" style={{ margin: 0 }}>Ordner enthalten nur Verknüpfungen – der Song existiert genau einmal.</p>
      {(folders ?? []).sort((a, b) => path(a).localeCompare(path(b), "de")).map((f) => (
        <label key={f.id} className="row" style={{ cursor: "pointer" }}>
          <input type="checkbox" checked={sel.includes(f.id)} onChange={() => setSel(sel.includes(f.id) ? sel.filter((x) => x !== f.id) : [...sel, f.id])} />
          {path(f)} {song.home_folder_id === f.id && <span className="badge info">Heimatordner</span>}
          {sel.includes(f.id) && song.home_folder_id !== f.id && <span className="badge">Link</span>}
        </label>
      ))}
      <div className="row"><span className="spacer" /><button className="btn primary" onClick={save}>Ordner speichern</button></div>
      <h3>Versionen</h3>
      {song.versions?.length ? (
        <>
          <p className="muted small" style={{ margin: 0 }}>Versionen teilen Ruhezeit, Spins und Separation; gespielt wird die am längsten ungespielte.</p>
          {song.versions.map((x: any) => <div key={x.id}>{x.artist ? `${x.artist} – ` : ""}{x.title} <span className="muted small">{fmtMs(x.duration_ms)}</span></div>)}
          <div className="row">
            <button className="btn small" onClick={async () => { await api(`/api/media/${song.id}/unversion`, { method: "POST" }); onSaved(); }}>Diesen Song lösen</button>
            <button className="btn small danger" onClick={async () => { await api(`/api/versions/${song.version_group_id}`, { method: "DELETE" }); onSaved(); }}>Gruppe auflösen</button>
          </div>
        </>
      ) : (
        <p className="muted small">Keine verknüpften Versionen. Versionen verknüpfst du in der Liste: mehrere Songs auswählen → „Als Versionen verknüpfen“.</p>
      )}
    </div>
  );
}

function Lookup({ song, onSaved }: { song: any; onSaved: () => void }) {
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    setBusy(true);
    try {
      setRows(await api(`/api/media/${song.id}/lookup`, { method: "POST" }));
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  const apply = async (r: any, cover: boolean) => {
    const body: any = {};
    if (r.album && !song.album) body.album = r.album;
    if (r.year && !song.year) body.year = r.year;
    if (r.isrc && !song.isrc) body.isrc = r.isrc;
    if (!song.artist && r.artist) body.artist = r.artist;
    try {
      if (Object.keys(body).length) await api(`/api/media/${song.id}`, { method: "PUT", body });
      if (cover && r.release_id) await api(`/api/media/${song.id}/cover-from-release`, { body: { release_id: r.release_id } });
      toast("Übernommen (nur leere Felder)");
      onSaved();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <div className="stack">
      <p className="muted small" style={{ margin: 0 }}>Sucht bei MusicBrainz nach „{song.artist} – {song.title}“. Übernommen werden nur Felder, die noch leer sind.</p>
      <button className="btn" onClick={search} disabled={busy}>{busy ? "Suche …" : "Tags nachschlagen"}</button>
      {rows && !rows.length && <div className="muted">Nichts gefunden.</div>}
      {rows?.map((r, i) => (
        <div key={i} className="row wrap" style={{ borderBottom: "1px solid var(--line)", padding: "6px 0" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <b>{r.artist} – {r.title}</b>
            <div className="small muted">{r.album} {r.year ? `(${r.year})` : ""} {r.isrc ? `· ${r.isrc}` : ""} {r.duration_ms ? `· ${fmtMs(r.duration_ms)}` : ""} · Treffer {r.score}%</div>
          </div>
          <button className="btn small" onClick={() => apply(r, false)}>Übernehmen</button>
          {r.release_id && <button className="btn small" onClick={() => apply(r, true)}>+ Cover</button>}
        </div>
      ))}
    </div>
  );
}

const TABS: [string, string][] = [["daten", "Daten"], ["cues", "Cues"], ["kategorien", "Kategorien"], ["ordner", "Ordner & Versionen"], ["zeiten", "Sendezeiten"], ["tags", "Tags"]];

export function SongEditor({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { data: song, reload } = useLoad<any>(`/api/media/${id}`, [id]);
  const [tab, setTab] = useState("daten");
  if (!song) return <div className="card">Lade …</div>;
  const saved = () => {
    reload();
    onChanged();
  };
  return (
    <div className="card" style={{ position: "sticky", top: 0 }}>
      <div className="row" style={{ marginBottom: 8 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{song.title}</h2>
          <div className="muted">{song.artist}</div>
        </div>
        <span className="spacer" />
        <OwnerSelect song={song} onSaved={saved} />
        <ContainerButton song={song} onSaved={saved} />
        <button className="btn small" title="Dauer, Lautheit, Wellenform und automatische Cues neu bestimmen (eigene Cues bleiben)" onClick={async () => { await api(`/api/media/${id}/analyze`, { body: {} }); toast("Analyse läuft"); setTimeout(saved, 4000); }}>Neu analysieren</button>
        <button className="iconbtn" onClick={onClose} aria-label="Schließen">✕</button>
      </div>
      <div className="tabs">
        {TABS.map(([k, l]) => <a key={k} href="#" onClick={(e) => { e.preventDefault(); setTab(k); }} className={tab === k ? "active" : ""}>{l}</a>)}
      </div>
      {tab === "daten" && <Data song={song} onSaved={saved} />}
      {tab === "cues" && <CueEditor song={song} onSaved={saved} />}
      {tab === "kategorien" && <Categories song={song} onSaved={saved} />}
      {tab === "ordner" && <FoldersVersions song={song} onSaved={saved} />}
      {tab === "zeiten" && (
        <div>
          <p className="small muted" style={{ marginTop: 0 }}>Wann dieser Titel überhaupt laufen darf – gilt überall (Kategorien, Pools, Rotation, Sendeplan). Aktuell: <b>{timingText(song)}</b></p>
          <TimingGrid key={song.id} value={song} saveLabel="Speichern" onSave={async (t) => { try { await api(`/api/media/${song.id}`, { method: "PUT", body: t }); toast("Sendezeiten gespeichert"); saved(); } catch (e: any) { toast(e.message, true); } }} />
        </div>
      )}
      {tab === "tags" && <Lookup song={song} onSaved={saved} />}
    </div>
  );
}


/** Which station the item belongs to (packaging) – or all stations (music). */
function OwnerSelect({ song, onSaved }: { song: any; onSaved: () => void }) {
  const { data: stations } = useLoad<any[]>("/api/stations");
  if ((stations?.length ?? 0) < 2 && !song.owner_station) return null;
  return (
    <select style={{ width: 170 }} value={song.owner_station ?? ""} title="Gehört zu"
      onChange={async (e) => { try { await api(`/api/media/${song.id}`, { method: "PUT", body: { owner_station: e.target.value || null } }); onSaved(); } catch (x: any) { toast(x.message, true); } }}>
      <option value="">alle Sender</option>
      {(stations ?? []).map((st) => <option key={st.id} value={st.id}>nur {st.name}</option>)}
    </select>
  );
}


/** A container opens its own editor (parts, silence, order). */
function ContainerButton({ song, onSaved }: { song: any; onSaved: () => void }) {
  const [is, setIs] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { api(`/api/containers/${song.id}`).then(() => setIs(true)).catch(() => setIs(false)); }, [song.id]);
  if (!is) return null;
  return (
    <>
      <button className="btn small" onClick={() => setOpen(true)}>Container bearbeiten</button>
      {open && <ContainerEditor id={song.id} onClose={(c) => { setOpen(false); if (c) onSaved(); }} />}
    </>
  );
}
